import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from "../App";


const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-base: #0f172a;
    --surface: #1e293b;
    --surface-hover: #334155;
    --border: #334155;
    --border-focus: #6366f1;
    --primary: #6366f1;
    --primary-hover: #4f46e5;
    --primary-dim: rgba(99, 102, 241, 0.15);
    --text-main: #f8fafc;
    --text-muted: #94a3b8;
    --error: #ef4444;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg-base);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }

  .auth-page::before {
    content: '';
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 80vw;
    height: 80vh;
    background: radial-gradient(circle, var(--primary-dim) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 3rem 2.5rem;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  }

  .auth-logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  .auth-logo-icon { width: 28px; height: 28px; color: var(--primary); }
  .auth-logo-text { font-size: 1.5rem; font-weight: 700; letter-spacing: -0.03em; color: var(--text-main); }

  .auth-header { text-align: center; margin-bottom: 2.5rem; }
  .auth-title { font-size: 1.75rem; font-weight: 600; color: var(--text-main); margin-bottom: 0.5rem; letter-spacing: -0.02em; }
  .auth-subtitle { color: var(--text-muted); font-size: 0.95rem; line-height: 1.5; }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    margin-bottom: 1.5rem;
  }

  .field-wrap {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    position: relative;
  }

  .field-label { font-size: 0.85rem; font-weight: 500; color: var(--text-main); }

  .auth-input {
    width: 100%;
    background: var(--bg-base);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    padding: 0.85rem 1rem;
    outline: none;
    transition: all 0.2s ease;
  }
  .auth-input:focus { border-color: var(--border-focus); box-shadow: 0 0 0 4px var(--primary-dim); }
  .auth-input::placeholder { color: var(--text-muted); }
  .auth-input.has-icon { padding-right: 2.8rem; }

  .eye-btn {
    position: absolute;
    right: 0.8rem;
    bottom: 0.8rem; /* Aligned for the new input height with label */
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.2s;
  }
  .eye-btn:hover { color: var(--text-main); }

  .field-error {
    font-size: 0.8rem;
    color: var(--error);
    margin-top: 0.3rem;
  }

  .terms-row {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 1.8rem;
  }
  .terms-row input[type="checkbox"] {
    accent-color: var(--primary);
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
    margin-top: 0.15rem;
    border-radius: 4px;
    border: 1px solid var(--border);
  }
  .terms-row label {
    font-size: 0.85rem;
    color: var(--text-muted);
    cursor: pointer;
    line-height: 1.5;
  }
  .terms-row a { color: var(--text-main); text-decoration: none; font-weight: 500; }
  .terms-row a:hover { color: var(--primary); }

  .auth-btn {
    width: 100%;
    background: var(--primary);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.85rem 1.5rem;
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  }
  .auth-btn:hover:not(:disabled) { background: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35); }
  .auth-btn:disabled { background: var(--surface-hover); color: var(--text-muted); cursor: not-allowed; box-shadow: none; transform: none; }

  .auth-footer-text { text-align: center; margin-top: 2rem; font-size: 0.9rem; color: var(--text-muted); }
  .auth-footer-text a { color: var(--text-main); text-decoration: none; font-weight: 500; transition: color 0.2s; }
  .auth-footer-text a:hover { color: var(--primary); }
`;

const Registration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    termsAndCondition: false,
  });
  const [errors, setErrors] = useState({ password: "", confirmPassword: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    if (name === "password") validatePassword(value);
    else if (name === "confirmPassword") validateConfirmPassword(value, formData.password);
  };

  const validatePassword = (password) => {
    const minLength = 8, maxLength = 64;
    const regexNumber = /\d/;
    const regexLowerCase = /[a-z]/;
    const regexUpperCase = /[A-Z]/;
    const regexSpecialChar = /[@#$%^&*(),.?":{}|<>]/;
    let errorMessage = "";
    if (password.length < minLength || password.length > maxLength) {
      errorMessage = `Password must be between ${minLength}-${maxLength} characters.`;
    } else if (!regexNumber.test(password)) {
      errorMessage = "Password must contain at least one number.";
    } else if (!regexLowerCase.test(password)) {
      errorMessage = "Password must contain at least one lowercase letter.";
    } else if (!regexUpperCase.test(password)) {
      errorMessage = "Password must contain at least one uppercase letter.";
    } else if (!regexSpecialChar.test(password)) {
      errorMessage = "Password must contain at least one special character.";
    }
    setErrors((prev) => ({ ...prev, password: errorMessage }));
    return errorMessage === "";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    const errorMessage = confirmPassword !== password ? "Passwords do not match." : "";
    setErrors((prev) => ({ ...prev, confirmPassword: errorMessage }));
    return errorMessage === "";
  };

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isPasswordValid = validatePassword(formData.password);
    const isConfirmPasswordValid = validateConfirmPassword(formData.confirmPassword, formData.password);
    if (!isPasswordValid || !isConfirmPasswordValid || !formData.termsAndCondition) {
      toast.error("Please correct the errors in the form and accept terms & conditions.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${StandardURL}/auth/register`,
        formData,
        { params: { accountType: "CUSTOMER" } }
      );
      toast.success("Registration successful. Check your email to complete verification!");
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        toast.error("Unauthorized access.");
      } else if (error.response && error.response.status === 406) {
        toast.error("Wrong password format. Password must contain a number, capital letter, small letter, and a special character.");
      } else {
        toast.error("An error occurred, please try again later.");
      }
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-container">
            <svg className="auth-logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span className="auth-logo-text">Multiphasic Infraconstruct</span>
          </div>

          <div className="auth-header">
            <h2 className="auth-title">Create an Account</h2>
            <p className="auth-subtitle">Join Multiphasic Infraconstruct and start managing your communications today.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="field-group">
              <div className="field-wrap">
                <label htmlFor="name" className="field-label">Full Name</label>
                <input
                  id="name" name="name" type="text" autoComplete="name" required
                  className="auth-input" placeholder="e.g. John Doe"
                  value={formData.name} onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <label htmlFor="email" className="field-label">Email Address</label>
                <input
                  id="email" name="email" type="email" autoComplete="email" required
                  className="auth-input" placeholder="name@company.com"
                  value={formData.email} onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <label htmlFor="phoneNumber" className="field-label">Phone Number</label>
                <input
                  id="phoneNumber" name="phoneNumber" type="tel" autoComplete="tel" required
                  className="auth-input" placeholder="+2547XXXXXXXX"
                  value={formData.phoneNumber} onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <label htmlFor="password" className="field-label">Password</label>
                <input
                  id="password" name="password"
                  type={passwordVisible ? "text" : "password"}
                  autoComplete="new-password" required
                  className="auth-input has-icon" placeholder="Create your password"
                  value={formData.password} onChange={handleChange}
                />
                <button type="button" className="eye-btn" onClick={togglePasswordVisibility}
                  aria-label={passwordVisible ? "Hide password" : "Show password"}>
                  <FontAwesomeIcon icon={passwordVisible ? faEye : faEyeSlash} style={{ width: 16, height: 16 }} />
                </button>
                {errors.password && <p className="field-error">{errors.password}</p>}
              </div>
              <div className="field-wrap">
                <label htmlFor="confirmPassword" className="field-label">Confirm Password</label>
                <input
                  id="confirmPassword" name="confirmPassword"
                  type={confirmPasswordVisible ? "text" : "password"}
                  autoComplete="new-password" required
                  className="auth-input has-icon" placeholder="Confirm your password"
                  value={formData.confirmPassword} onChange={handleChange}
                />
                <button type="button" className="eye-btn" onClick={toggleConfirmPasswordVisibility}
                  aria-label={confirmPasswordVisible ? "Hide password" : "Show password"}>
                  <FontAwesomeIcon icon={confirmPasswordVisible ? faEye : faEyeSlash} style={{ width: 16, height: 16 }} />
                </button>
                {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="terms-row">
              <input
                id="terms-and-condition" name="termsAndCondition" type="checkbox"
                checked={formData.termsAndCondition} onChange={handleChange} required
              />
              <label htmlFor="terms-and-condition">
                I agree to the{" "}
                <a href="#">Terms and Conditions</a>
              </label>
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={isLoading || !!errors.password || !!errors.confirmPassword || !formData.termsAndCondition}
            >
              {isLoading ? (
                <Oval height={20} width={20} color="#ffffff" visible={true} ariaLabel="oval-loading" secondaryColor="rgba(255,255,255,0.3)" strokeWidth={4} strokeWidthSecondary={4} />
              ) : (
                "Register Account"
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Already have an account?{" "}
            <a href="/">Log In</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default Registration;